import React, { FC } from 'react'

import { useForm, useStore } from '@tanstack/react-form'
import {
  AuthButton,
  AuthButtonColor,
  AuthButtonVariant,
  AuthInput,
  ErrorAlert
} from 'modules/auth/components'
import { motion } from 'motion/react'
import { useTranslation } from 'react-i18next'
import { useOauthRedirectMutation } from 'shared/api/auth/requests'
import { onLoginError } from 'shared/api/auth/utils/onLoginError'
import { EASE_IN_OUT } from 'shared/constants/apps'
import { FEATURE_FLAGS } from 'shared/constants/featureFlags'
import { LocalStorageKey } from 'shared/constants/localStorage'
import { lsSetItem } from 'shared/utils/local-storage'

import styles from '../../LoginForm.module.scss'
import { ssoLoginSchema } from './SsoLogin.schema'

const inputAnimation = {
  initial: { y: 12, scale: 0.98 },
  animate: {
    y: 0,
    scale: 1,
    transition: {
      duration: 0.4,
      ease: EASE_IN_OUT
    }
  }
}

export const SsoLogin: FC = () => {
  const { t } = useTranslation('auth', {
    keyPrefix: 'new_login'
  })
  const oauthRedirectMutation = useOauthRedirectMutation({
    onSuccess: data => {
      window.location.href = data.redirect_url
    },
    onError: onLoginError
  })

  const ssoForm = useForm({
    defaultValues: {
      email: ''
    },
    validators: {
      onMount: ssoLoginSchema,
      onChange: ssoLoginSchema,
      onSubmit: ssoLoginSchema
    },
    onSubmit: values => {
      if (FEATURE_FLAGS.SHOW_REMEMBER_ME) {
        try {
          lsSetItem(LocalStorageKey.REMEMBER_ME, true)
        } catch {
          // Swallow storage errors to avoid blocking login
        }
      }
      oauthRedirectMutation.mutate({
        auth_method: 'email',
        account_type: 'organizational',
        front_redirect_url: window.location.href,
        email: values.value.email
      })
    }
  })

  const ssoFieldMeta = useStore(ssoForm.store, state => state.fieldMeta)
  const email = ssoFieldMeta.email

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    e.stopPropagation()
    ssoForm.handleSubmit()
  }

  return (
    <form className={styles.loginForm} onSubmit={handleSubmit}>
      <motion.div className={styles.inputs} {...inputAnimation}>
        <ssoForm.Field name='email'>
          {field => (
            <AuthInput
              placeholder={t('login.email_placeholder')}
              className={styles.input}
              value={field.state.value}
              onChange={e => field.handleChange(e.target.value)}
              onBlur={field.handleBlur}
              type='email'
              required
            />
          )}
        </ssoForm.Field>
        <ErrorAlert
          message={
            oauthRedirectMutation.error?.response?.status === 404
              ? t('sso_provider_not_found')
              : ''
          }
        />
        <AuthButton
          className={styles.loginButton}
          label={t('login.button_text')}
          variant={AuthButtonVariant.FILLED_BLUE}
          colorType={AuthButtonColor.GHOST_DARKER}
          isLoading={oauthRedirectMutation.isPending}
          fullWidth={true}
          isBold
          disabled={!(email?.isTouched && email?.isValid)}
          type='submit'
        />
      </motion.div>
    </form>
  )
}
